import { ChangeEvent, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import validator from 'validator';

import { SocialButton } from '../components/SocialButton';
import { GoogleLogo } from '../components/GoogleLogo';
import { AppleLogo } from '../components/AppleLogo';
import { passwordRules } from '../config/passwordRules';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import useBonnierPixel from '../hooks/useBonnierPixel';
import { Container } from 'react-bootstrap';

export const RegisterPage = () => {
  const { t } = useTranslation();
  const alert = useAlert();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [emailConfirm, setEmailConfirm] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [errors, setErrors] = useState<any>({});
  const [validated, setValidated] = useState<boolean>(false);

  useBonnierPixel('begin register', true);

  const nameChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setName(ev.target.value);
    setValidated(false);
  };

  const emailChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setEmail(ev.target.value);
    setValidated(false);
  };

  const emailConfirmChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setEmailConfirm(ev.target.value);
    setValidated(false);
  };

  const passwordChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setPassword(ev.target.value);
    setValidated(false);
  };

  const passwordConfirmChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setPasswordConfirm(ev.target.value);
    setValidated(false);
  };

  const onFormSubmit = async (ev: FormEvent) => {
    ev.preventDefault();

    setIsLoading(true);
    let validationErrors = {};

    if (!name.match(/^.{1,24}$/)) {
      validationErrors = { ...validationErrors, name: t('auth.invalid-name') };
    }
    if (!validator.isEmail(email)) {
      validationErrors = {
        ...validationErrors,
        email: t('auth.invalid-email')
      };
    }
    if (email !== emailConfirm) {
      validationErrors = {
        ...validationErrors,
        emailConfirm: t('auth.email-not-matching')
      };
    }
    if (!validator.isStrongPassword(password, passwordRules)) {
      validationErrors = {
        ...validationErrors,
        password: t('auth.invalid-password', passwordRules)
      };
    }
    if (password !== passwordConfirm) {
      validationErrors = {
        ...validationErrors,
        passwordConfirm: t('auth.password-not-matching')
      };
    }

    setErrors(validationErrors);
    setValidated(true);

    if (Object.keys(validationErrors).length === 0) {
      createUserWithEmailAndPassword(auth, email, password)
        .then(async (result) => {
          await updateProfile(result.user, { displayName: name?.trim() });
          if (!result.user.emailVerified) {
            await sendEmailVerification(result.user, {
              url: window.location.href.replace(/\/register\/?$/, '')
            });
          }
          navigate('/?isNewAccount=true', { replace: true });
        })
        .catch((error) => {
          setIsLoading(false);
          if (error.code === 'auth/email-already-in-use') {
            alert.error(t('auth.email-already-in-use'));
          } else {
            alert.error(t('unknown-error'));
          }
        });
    } else {
      setIsLoading(false);
    }
  };

  const loginWithGoogleClicked = () => {
    setIsLoading(true);
    auth.useDeviceLanguage();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    signInWithPopup(auth, provider)
      .then(() => {
        navigate('/?isNewAccount=true', { replace: true });
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('loginWithGoogleClicked', error);
      });
  };

  const loginWithAppleClicked = () => {
    setIsLoading(true);
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    signInWithPopup(auth, provider)
      .then(() => {
        navigate('/?isNewAccount=true', { replace: true });
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('loginWithAppleClicked', error);
      });
  };

  const formatTermsAndConditions = (text: string) => {
    return text.replace(
      /\{(.*?)\}/g,
      (_, p1) => `<a href="https://enzoway.com/privacy-policy/" target="_blank" rel="noreferrer">${p1}</a>`
    );
  };

  return (
    <Container>
      <form onSubmit={onFormSubmit} noValidate>
        <h1 className='h3 mb-3'>{t('registerpage.title')}</h1>

        <div className='mb-3'>{t('registerpage.description')}</div>

        <div className='w-100'>
          <SocialButton
            logo={<GoogleLogo />}
            text={t('registerpage.sign-in-with-google')}
            disabled={isLoading}
            onClick={loginWithGoogleClicked}
          />
          <SocialButton
            logo={<AppleLogo />}
            text={t('registerpage.sign-in-with-apple')}
            disabled={isLoading}
            onClick={loginWithAppleClicked}
          />
        </div>

        <div className='d-flex text-center py-3'>
          <hr className='flex-grow-1' />
          <span className='align-self-center mx-3 text-muted text-uppercase'>{t('loginpage.or')}</span>
          <hr className='flex-grow-1' />
        </div>

        <label className='d-flex mt-3'>{t('registerpage.name')}</label>
        <input
          autoFocus
          required
          type='text'
          className={['form-control my-2', validated && errors.name ? 'is-invalid' : null].filter((p) => p).join(' ')}
          placeholder={t('registerpage.name-placeholder')}
          value={name}
          onChange={nameChanged}
        />
        <div className='d-flex invalid-feedback'>{errors.name}</div>

        <label className='d-flex mt-3'>{t('registerpage.email')}</label>
        <input
          required
          type='email'
          className={['form-control my-2', validated && errors.email ? 'is-invalid' : null].filter((p) => p).join(' ')}
          placeholder={t('registerpage.email')}
          value={email}
          onChange={emailChanged}
        />
        <div className='d-flex invalid-feedback'>{errors.email}</div>

        <label className='d-flex mt-3'>{t('registerpage.email-confirm')}</label>
        <input
          required
          type='email'
          className={['form-control my-2', validated && errors.emailConfirm ? 'is-invalid' : null]
            .filter((p) => p)
            .join(' ')}
          placeholder={t('registerpage.email-confirm')}
          value={emailConfirm}
          onChange={emailConfirmChanged}
        />
        <div className='d-flex invalid-feedback'>{errors.emailConfirm}</div>

        <label className='d-flex mt-3'>{t('registerpage.password')}</label>
        <input
          required
          type='password'
          className={['form-control my-2', validated && errors.password ? 'is-invalid' : null]
            .filter((p) => p)
            .join(' ')}
          placeholder={t('registerpage.password')}
          value={password}
          onChange={passwordChanged}
          autoComplete='new-password'
        />
        <div className='d-flex invalid-feedback' dangerouslySetInnerHTML={{ __html: errors.password }} />

        <label className='d-flex mt-3'>{t('registerpage.password-confirm')}</label>
        <input
          required
          type='password'
          className={['form-control my-2', validated && errors.passwordConfirm ? 'is-invalid' : null]
            .filter((p) => p)
            .join(' ')}
          placeholder={t('registerpage.password-confirm')}
          value={passwordConfirm}
          onChange={passwordConfirmChanged}
          autoComplete='new-password'
        />
        <div className='d-flex invalid-feedback' dangerouslySetInnerHTML={{ __html: errors.passwordConfirm }} />

        <p
          className='fst-italic fw-light mt-3 small text-start'
          dangerouslySetInnerHTML={{
            __html: formatTermsAndConditions(t('registerpage.terms-and-conditions'))
          }}
        />

        <button type='submit' className='btn btn-primary w-100 my-3' disabled={isLoading}>
          {t('registerpage.submit')}
        </button>

        <Link to='/login'>{t('back')}</Link>
      </form>{' '}
    </Container>
  );
};
